import { ethers } from "ethers";

import {
  OSHAR_REAL_ESTATE_ABI,
  OSHAR_REAL_ESTATE_CONTRACT_ADDRESS,
  OSHAR_REAL_ESTATE_CHAIN_ID,
  SEPOLIA_USDC_ADDRESS,
  getOsharRealEstateRpcUrl,
} from "./contractConfig";

const rpcUrl = getOsharRealEstateRpcUrl();
const provider = new ethers.providers.JsonRpcProvider(rpcUrl, OSHAR_REAL_ESTATE_CHAIN_ID);
const contract = new ethers.Contract(
  OSHAR_REAL_ESTATE_CONTRACT_ADDRESS,
  OSHAR_REAL_ESTATE_ABI,
  provider
);
const ERC20_ABI = [
  "function allowance(address owner, address spender) view returns (uint256)",
  "function balanceOf(address owner) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
];

function assertContractReady() {
  if (!String(OSHAR_REAL_ESTATE_CONTRACT_ADDRESS || "").trim()) {
    throw new Error(
      "Missing Oshar real estate contract address (REACT_APP_OSHAR_REAL_ESTATE_CONTRACT_ADDRESS or REACT_APP_OSHAR_SEPOLIA_CONTRACT_ADDRESS)."
    );
  }
}

export async function readOwnerOf(tokenId) {
  assertContractReady();
  return contract.ownerOf(tokenId);
}

export async function readTokenURI(tokenId) {
  assertContractReady();
  return contract.tokenURI(tokenId);
}

export async function readTokenEthPrice(tokenId) {
  assertContractReady();
  return contract.tokenEthPrice(tokenId);
}

export async function readTokenUsdcPrice(tokenId) {
  assertContractReady();
  return contract.tokenUsdcPrice(tokenId);
}

export async function isTokenMinted(tokenId) {
  try {
    await readOwnerOf(tokenId);
    return true;
  } catch (error) {
    if (error?.code === "CALL_EXCEPTION") return false;
    throw error;
  }
}

function assertWalletProvider() {
  if (!window?.ethereum) {
    throw new Error("No wallet provider found. Connect a wallet first.");
  }
}

function getSigner() {
  assertWalletProvider();
  const browserProvider = new ethers.providers.Web3Provider(window.ethereum);
  return browserProvider.getSigner();
}

function getWriteContract(signer) {
  return new ethers.Contract(OSHAR_REAL_ESTATE_CONTRACT_ADDRESS, OSHAR_REAL_ESTATE_ABI, signer);
}

function getUsdcContract(signerOrProvider) {
  return new ethers.Contract(SEPOLIA_USDC_ADDRESS, ERC20_ABI, signerOrProvider);
}

/**
 * @param {number|string} tokenId
 * @param {import("ethers").BigNumber} priceWei
 * @param {{ afterSubmit?: (tx: { wait: Function, hash?: string }) => void }} [hooks]
 */
export async function mintWithETH(tokenId, priceWei, hooks = {}) {
  const signer = getSigner();
  const writeContract = getWriteContract(signer);
  const tx = await writeContract.mintWithETH(tokenId, { value: priceWei });
  if (typeof hooks?.afterSubmit === "function") {
    try {
      hooks.afterSubmit(tx);
    } catch {
      /* UI hook must not break mint */
    }
  }
  const receipt = await tx.wait();
  return { txHash: tx.hash, receipt };
}

export async function readUSDCBalance(walletAddress) {
  if (!walletAddress) return ethers.constants.Zero;
  const readProvider = provider || new ethers.providers.Web3Provider(window.ethereum);
  const usdc = getUsdcContract(readProvider);
  return usdc.balanceOf(walletAddress);
}

export async function readUSDCAllowance(
  walletAddress,
  spender = OSHAR_REAL_ESTATE_CONTRACT_ADDRESS
) {
  if (!walletAddress) return ethers.constants.Zero;
  const readProvider = provider || new ethers.providers.Web3Provider(window.ethereum);
  const usdc = getUsdcContract(readProvider);
  return usdc.allowance(walletAddress, spender);
}

export async function approveUSDC(spender = OSHAR_REAL_ESTATE_CONTRACT_ADDRESS, amount) {
  const signer = getSigner();
  const usdc = getUsdcContract(signer);
  const tx = await usdc.approve(spender, amount);
  const receipt = await tx.wait();
  return { txHash: tx.hash, receipt };
}

export async function mintWithUSDC(tokenId) {
  const signer = getSigner();
  const writeContract = getWriteContract(signer);
  const tx = await writeContract.mintWithUSDC(tokenId);
  const receipt = await tx.wait();
  return { txHash: tx.hash, receipt };
}
